import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH14_QUESTIONS } from "../data/neetPhysicsCh14Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 14: Oscillations (NCERT Class 11).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Periodic Motion & SHM Basics", "SHM Equations & Energy", "Simple Pendulum & Spring-Mass Systems", "Damped & Forced Oscillations"];

const SECTION_COLORS = {
  "Periodic Motion & SHM Basics":            "#0891b2",
  "SHM Equations & Energy":                  "#9333ea",
  "Simple Pendulum & Spring-Mass Systems":   "#16a34a",
  "Damped & Forced Oscillations":            "#ea580c",
};

const CONCEPTS = [
  // ── Periodic Motion & SHM Basics ────────────────────────────────────────────
  { section: "Periodic Motion & SHM Basics", title: "Periodic and oscillatory motion",
    explanation: "A motion is PERIODIC if it repeats itself identically after equal intervals of time, called the period (T). A motion is OSCILLATORY (or vibratory) if it is periodic AND the body moves back and forth repeatedly about a fixed mean (equilibrium) position. Every oscillatory motion is periodic, but not every periodic motion is oscillatory -- uniform circular motion, for instance, repeats after each revolution but the object never reverses direction about a mean position.",
    example: `Periodic + oscillatory: simple pendulum, mass on a spring,
                          vibrating guitar string
Periodic but NOT oscillatory: uniform circular motion,
                          Earth's revolution around the Sun` },
  { section: "Periodic Motion & SHM Basics", title: "Simple harmonic motion (SHM) — defining equation a = -ω²x",
    explanation: "SHM is a special periodic motion in which the restoring force (and hence acceleration) is directly proportional to the displacement x from the mean position, and always directed OPPOSITE to that displacement (back toward the mean position): F = -kx, so a = F/m = -(k/m)x = -ω²x, where ω² = k/m is a positive constant. The minus sign is essential -- it shows the force always pulls the body back, never pushes it further away.",
    example: `F = -kx  ->  a = -(k/m)x = -ω²x     (ω² = k/m)

If k = 50 N/m and m = 2 kg:
ω² = 50/2 = 25 (rad/s)^2  ->  ω = 5 rad/s` },
  { section: "Periodic Motion & SHM Basics", title: "Amplitude, phase and angular frequency",
    explanation: "In x(t) = A sin(ωt + φ): A (amplitude) is the maximum displacement from the mean position; the quantity (ωt + φ) is the PHASE, which fixes the position and direction of motion at any instant; φ is the phase constant (the phase at t = 0), fixed by the initial position/velocity; ω is the angular frequency, the rate at which the phase changes, measured in rad/s.",
    example: `x(t) = A sin(ωt + φ)
At t = 0: x(0) = A sin(φ)

If A = 0.1 m and φ = π/6 (30°):
x(0) = 0.1 x sin(30°) = 0.1 x 0.5 = 0.05 m` },
  { section: "Periodic Motion & SHM Basics", title: "Time period and frequency — T = 2π/ω, f = 1/T",
    explanation: "The time period T is the time taken for one complete oscillation; frequency f is the number of oscillations per unit time (unit: hertz, Hz). Angular frequency ω, period T and frequency f all describe the same rate of oscillation: ω = 2π/T = 2πf, so T = 2π/ω and f = 1/T = ω/2π.",
    example: `If ω = 10 rad/s:
T = 2π/ω = 2π/10 = 0.628 s
f = 1/T = 1/0.628 = 1.59 Hz   (check: f = ω/2π = 10/6.283 = 1.59 Hz [correct])` },

  // ── SHM Equations & Energy ───────────────────────────────────────────────────
  { section: "SHM Equations & Energy", title: "Displacement as a function of time — x(t) = A sin(ωt + φ)",
    explanation: "Solving the SHM equation a = -ω²x gives x(t) = A sin(ωt + φ) (equivalently written with cosine), where A, ω and φ are all fixed by the physical setup and the initial conditions (position and velocity at t = 0).",
    example: `A pendulum released from its extreme position x = A at t = 0
(so velocity = 0 there) needs x(0) = A sin(φ) = A, i.e. sin(φ) = 1,
so φ = π/2.
Hence  x(t) = A sin(ωt + π/2) = A cos(ωt)` },
  { section: "SHM Equations & Energy", title: "Velocity in SHM — v(t) = Aω cos(ωt+φ) = ω√(A²−x²)",
    explanation: "Differentiating x(t) with respect to time gives v(t) = Aω cos(ωt + φ). Using sin²+cos²=1, this can be rewritten in terms of displacement as v = ω√(A² − x²). Velocity is MAXIMUM (= Aω) at the mean position (x = 0) and ZERO at the extreme positions (x = ±A).",
    example: `A = 5 cm, ω = 2 rad/s
v_max = Aω = 5 x 2 = 10 cm/s   (at x = 0)

At x = 3 cm:
v = ω√(A² − x²) = 2 x √(25 − 9) = 2 x 4 = 8 cm/s` },
  { section: "SHM Equations & Energy", title: "Acceleration in SHM — a(t) = -Aω² sin(ωt+φ) = -ω²x",
    explanation: "Differentiating v(t) gives a(t) = -Aω² sin(ωt + φ) = -ω²x. Acceleration is ZERO at the mean position and MAXIMUM in magnitude (= Aω²) at the extreme positions, always directed toward the mean position (that's why it's called the restoring acceleration).",
    example: `Same A = 5 cm, ω = 2 rad/s as above:
a_max = Aω² = 5 x 4 = 20 cm/s²   (at the extremes, x = ±5 cm)

At x = 3 cm:
a = -ω²x = -(4)(3) = -12 cm/s²   (directed back toward x = 0)` },
  { section: "SHM Equations & Energy", title: "Energy in SHM — KE + PE = constant = ½kA²",
    explanation: "In SHM (no friction), total mechanical energy is conserved: E = KE + PE = ½kA², the same at every instant. At displacement x: PE = ½kx², KE = ½k(A² − x²). At the mean position (x=0), KE is maximum and PE = 0; at the extremes (x=±A), PE is maximum and KE = 0.",
    example: `k = 200 N/m, A = 0.1 m
Total energy E = ½kA² = ½(200)(0.1)² = 1 J

At x = A/2 = 0.05 m:
PE = ½kx² = ½(200)(0.05)² = 0.25 J
KE = E − PE = 1 − 0.25 = 0.75 J
(KE : PE = 3 : 1 at x = A/2 — this 3:1 ratio holds for ANY k, A)` },
  { section: "SHM Equations & Energy", title: "Graphs of KE and PE vs displacement and vs time",
    explanation: "Plotted against displacement x, PE = ½kx² is an upward parabola (minimum at x=0), and KE = ½k(A²−x²) is an inverted (downward) parabola (maximum at x=0, zero at x=±A) -- their sum is always the flat horizontal line E. Plotted against TIME, both KE and PE oscillate with TWICE the angular frequency of x(t) itself, because they depend on sin² or cos² terms.",
    example: `x(t) = A sin(ωt)
PE(t) = ½kA² sin²(ωt) = ¼kA²[1 − cos(2ωt)]

The "2ωt" shows PE (and likewise KE) completes TWO full
cycles for every ONE cycle of the displacement x(t).` },

  // ── Simple Pendulum & Spring-Mass Systems ───────────────────────────────────
  { section: "Simple Pendulum & Spring-Mass Systems", title: "Simple pendulum — T = 2π√(L/g) and its assumptions",
    explanation: "For a bob of length L displaced through a small angle θ, the restoring torque produces angular SHM with ω² = g/L, giving T = 2π√(L/g). This holds only under the assumptions: the string is inextensible and massless, the bob is a point mass, the angular amplitude is small (θ generally < ~10°, so sinθ ≈ θ in radians), and air resistance/friction at the support is negligible.",
    example: `T = 2π√(L/g)

L = 1 m, g = 9.8 m/s²:
T = 2π√(1/9.8) = 2π(0.319) ≈ 2.01 s

For a "seconds pendulum" (T = 2 s exactly):
L = g(T/2π)² = 9.8 x (2/6.283)² ≈ 0.993 m ≈ 99.3 cm` },
  { section: "Simple Pendulum & Spring-Mass Systems", title: "Spring-mass system — T = 2π√(m/k), horizontal and vertical",
    explanation: "A mass m on a spring of force constant k obeys F = -kx, giving ω² = k/m and T = 2π√(m/k). This period is the SAME whether the spring-mass system is horizontal or vertical -- hanging the mass vertically just shifts the equilibrium (mean) position down by mg/k (where the spring's extra tension balances gravity); the restoring force about this NEW mean position is still exactly -kx, so gravity does not change the period at all.",
    example: `T = 2π√(m/k)

m = 0.5 kg, k = 200 N/m:
T = 2π√(0.5/200) = 2π√(0.0025) = 2π(0.05) ≈ 0.314 s` },
  { section: "Simple Pendulum & Spring-Mass Systems", title: "Combining springs — series and parallel",
    explanation: "For two springs in PARALLEL (both stretch equally, sharing the load), the effective constant ADDS: k_eff = k1 + k2 (stiffer combination, shorter period). For two springs in SERIES (same force through both, extensions add), the effective constant follows 1/k_eff = 1/k1 + 1/k2, i.e. k_eff = k1k2/(k1+k2) (softer combination, longer period).",
    example: `k1 = 100 N/m, k2 = 200 N/m
Parallel: k_eff = 100 + 200 = 300 N/m
Series:   k_eff = (100x200)/(100+200) = 66.7 N/m

With m = 2 kg:
T_parallel = 2π√(2/300)  ≈ 0.513 s   (stiffer -> faster)
T_series   = 2π√(2/66.7) ≈ 1.088 s   (softer -> slower)` },
  { section: "Simple Pendulum & Spring-Mass Systems", title: "Pendulum period is independent of mass and amplitude",
    explanation: "T = 2π√(L/g) contains no mass term at all -- a heavier or lighter bob on the same length string swings with exactly the same period (for small amplitude). It is also independent of the amplitude of swing, as long as the amplitude stays small. Because T depends only on L and g, a pendulum clock keeping correct time at one location runs at a slightly different rate if taken to a place with a different value of g (e.g. higher altitude, or a different planet).",
    example: `A pendulum clock is correct at g = 9.80 m/s² (L unchanged).
Moved to a location with g' = 9.75 m/s²:
T'/T = √(g/g') = √(9.80/9.75) ≈ 1.0026

The period increases by about 0.26% -> the clock now runs
slightly SLOW (loses time) at the new location.` },

  // ── Damped & Forced Oscillations ────────────────────────────────────────────
  { section: "Damped & Forced Oscillations", title: "Damped oscillations — amplitude decays with time",
    explanation: "A REAL oscillator always experiences some dissipative (resistive) force -- friction, air resistance, viscous drag -- which removes mechanical energy from the system on every cycle. This makes the amplitude decrease progressively with time (the oscillation is said to be DAMPED); for many common damping forces the amplitude decays exponentially, and each successive swing is smaller than the one before by roughly the same fraction. Eventually all the mechanical energy is dissipated and the motion stops.",
    example: `Amplitude of successive swings, each ~90% of the one before:
Swing 1: 10 cm
Swing 2: 10 x 0.9 = 9 cm
Swing 3: 9 x 0.9  = 8.1 cm
Swing 4: 8.1 x 0.9 = 7.29 cm    <- geometric (exponential) decay` },
  { section: "Damped & Forced Oscillations", title: "Forced oscillations — driven at an external frequency",
    explanation: "If left undisturbed, a system oscillates at its own NATURAL frequency ω0 (set by its mass/length and stiffness/gravity). If instead a periodic external force of a DIFFERENT frequency ωd is continuously applied, after the initial transient dies out the system settles into FORCED oscillations at the driver's frequency ωd (not at ω0), with an amplitude that depends on how close ωd is to ω0 and on the amount of damping present.",
    example: `Natural frequency of a swing (playground): ω0
Driving frequency of your pushes: ωd

After a few pushes (transient), the swing oscillates steadily
at ωd (the rate YOU push it), not at its own ω0 -- unless
you stop pushing, at which point it settles back toward ω0's
free-oscillation behaviour and decays (damped) toward rest.` },
  { section: "Damped & Forced Oscillations", title: "Resonance — maximum amplitude when ωd ≈ ω0",
    explanation: "Resonance occurs when the driving frequency ωd is equal to (or very close to) the system's natural frequency ω0: the forced oscillation's amplitude then becomes very large, limited only by the amount of damping present. This is why pushing a swing in rhythm with its own natural frequency builds up a large swing from small pushes, while pushing at a mismatched frequency barely moves it.",
    example: `Amplitude of forced oscillation is largest when ωd = ω0

Classic examples of resonance:
- Soldiers breaking step crossing a bridge (avoiding matching
  the bridge's natural frequency)
- Shattering a glass with a sound at its natural frequency
- Tuning a radio circuit to a station's broadcast frequency` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh14Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh14Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh14Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH14_QUESTIONS : NEET_PHYSICS_CH14_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 14</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Oscillations</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11, Ch. 14 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH14_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH14_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
